import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { AppCard } from '../ui/AppCard';
import { AI_PROVIDER_LABEL } from '../../types';
import {
  fetchReportSchedule,
  saveReportSchedule,
  type ReportSchedule,
  type ReportScheduleInput,
  type ReportScheduleMode,
} from '../../api/reportApi';

// Owner-only "Automatic reports" control for one channel: a daily or weekly
// digest at a chosen local time, or an auto-refresh that reports whenever new
// signals arrive (debounced). Backed by POST /channels/{n}/report-schedule -
// see backend/internal/report/schedule.go for the exact semantics (a schedule
// spends the owner's normal report allowance, skips an empty period, and
// never stacks a report behind one that hasn't finished).

const MODE_LABEL: Record<ReportScheduleMode, string> = {
  off: 'Off',
  daily: 'Daily digest',
  weekly: 'Weekly digest',
  on_new: 'When new signals arrive',
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEBOUNCE_OPTIONS = [
  { minutes: 15, label: 'at most every 15 minutes' },
  { minutes: 30, label: 'at most every 30 minutes' },
  { minutes: 60, label: 'at most hourly' },
  { minutes: 180, label: 'at most every 3 hours' },
  { minutes: 360, label: 'at most every 6 hours' },
  { minutes: 1440, label: 'at most daily' },
];

const PERIOD_OPTIONS = [
  { days: 1, label: 'the last day' },
  { days: 7, label: 'the last 7 days' },
  { days: 30, label: 'the last 30 days' },
  { days: 90, label: 'the last 90 days' },
];

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

// "queued" / "skipped: no new signals" -> a human line.
export function describeLastRun(s: ReportSchedule): string | null {
  if (!s.lastResult) return null;
  const when = new Date(s.lastRunAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  if (s.lastResult === 'queued') return `Last report queued ${when}.`;
  const why = s.lastResult.replace(/^skipped:\s*/, '');
  const reason: Record<string, string> = {
    'no new signals': 'nothing new to report on',
    quota: "you're out of reports this week",
    nokey: 'the provider needs an API key a schedule can’t hold',
  };
  return `Last check ${when}: skipped — ${reason[why] ?? why}.`;
}

const selectCls =
  'rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-2.5 py-1.5 text-sm outline-none focus:border-blue-500 disabled:opacity-50';

export function AutoReportsCard({ channelId }: { channelId: string }) {
  const [schedule, setSchedule] = useState<ReportSchedule | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setLoaded(false);
    setSchedule(null);
    setError(null);
    fetchReportSchedule(channelId)
      .then((s) => alive.current && setSchedule(s))
      .catch((e) => alive.current && setError(e instanceof Error ? e.message : 'Could not load the schedule.'))
      .finally(() => alive.current && setLoaded(true));
    return () => {
      alive.current = false;
    };
  }, [channelId]);

  const mode: ReportScheduleMode = schedule?.mode ?? 'off';

  // Every change is one POST with the full desired schedule: the current
  // values plus the edit. Selecting a mode from "off" seeds sensible defaults
  // (server-side defaults would also do; sending the browser's own timezone is
  // what matters, so daily 09:00 means the owner's 09:00).
  const save = useCallback(
    async (patch: Partial<ReportScheduleInput>) => {
      const base: ReportScheduleInput = schedule
        ? {
            mode: schedule.mode,
            provider: schedule.provider,
            periodDays: schedule.periodDays,
            hour: schedule.hour,
            weekday: schedule.weekday,
            timezone: schedule.timezone,
            debounceMinutes: schedule.debounceMinutes,
          }
        : { mode: 'off', hour: 9, weekday: 1, timezone: browserTimezone() };
      const next: ReportScheduleInput = { ...base, ...patch };
      // Switching modes drops a period that doesn't fit the new mode's
      // default (a daily digest wants "the last day", weekly/auto 7 days).
      if (patch.mode && patch.mode !== base.mode && patch.periodDays === undefined) {
        delete next.periodDays;
      }
      setSaving(true);
      setError(null);
      try {
        const stored = await saveReportSchedule(channelId, next);
        if (alive.current) setSchedule(stored);
      } catch (e) {
        if (alive.current) setError(e instanceof Error ? e.message : 'Could not save the schedule.');
      } finally {
        if (alive.current) setSaving(false);
      }
    },
    [channelId, schedule]
  );

  if (!loaded) return null;

  const lastRun = schedule ? describeLastRun(schedule) : null;

  return (
    <AppCard className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <CalendarClock size={16} className="text-black/50 dark:text-white/50" />
        <span className="text-sm font-semibold">Automatic reports</span>
        {saving && <span className="ml-auto text-[11px] text-black/40 dark:text-white/40">Saving…</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select
          aria-label="Automatic report mode"
          value={mode}
          disabled={saving}
          onChange={(e) => save({ mode: e.target.value as ReportScheduleMode })}
          className={selectCls}
        >
          {(Object.keys(MODE_LABEL) as ReportScheduleMode[]).map((m) => (
            <option key={m} value={m}>
              {MODE_LABEL[m]}
            </option>
          ))}
        </select>

        {schedule && schedule.mode === 'weekly' && (
          <>
            <span className="text-black/50 dark:text-white/50">on</span>
            <select
              aria-label="Day of week"
              value={schedule.weekday}
              disabled={saving}
              onChange={(e) => save({ weekday: Number(e.target.value) })}
              className={selectCls}
            >
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </>
        )}

        {schedule && (schedule.mode === 'daily' || schedule.mode === 'weekly') && (
          <>
            <span className="text-black/50 dark:text-white/50">at</span>
            <select
              aria-label="Hour"
              value={schedule.hour}
              disabled={saving}
              onChange={(e) => save({ hour: Number(e.target.value) })}
              className={selectCls}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
          </>
        )}

        {schedule && schedule.mode === 'on_new' && (
          <select
            aria-label="Minimum time between reports"
            value={schedule.debounceMinutes}
            disabled={saving}
            onChange={(e) => save({ debounceMinutes: Number(e.target.value) })}
            className={selectCls}
          >
            {DEBOUNCE_OPTIONS.map((o) => (
              <option key={o.minutes} value={o.minutes}>
                {o.label}
              </option>
            ))}
          </select>
        )}

        {schedule && (
          <>
            <span className="text-black/50 dark:text-white/50">covering</span>
            <select
              aria-label="Period each report covers"
              value={schedule.periodDays}
              disabled={saving}
              onChange={(e) => save({ periodDays: Number(e.target.value) })}
              className={selectCls}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <span className="text-[11px] text-black/40 dark:text-white/40">
        {!schedule
          ? 'Off. Turn on a digest, or have a report written whenever new signals arrive.'
          : schedule.mode === 'on_new'
            ? 'A report is queued after new signals arrive, no more often than the limit above.'
            : `Runs at ${hourLabel(schedule.hour)} (${schedule.timezone})${schedule.mode === 'weekly' ? ` on ${WEEKDAYS[schedule.weekday]}s` : ' every day'}. Skipped when nothing new came in.`}{' '}
        {schedule && `Uses ${AI_PROVIDER_LABEL[schedule.provider] ?? schedule.provider} and counts against your report allowance.`}
      </span>
      {lastRun && <span className="text-[11px] text-black/50 dark:text-white/50">{lastRun}</span>}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </AppCard>
  );
}
